import React, { createContext, useContext } from "react";

// Color palette interface
interface ColorPalette {
  coralPink: string;
  forestGreen: string;
  deepTeal: string;
  burgundy: string;
  wineRed: string;
  chocolate: string;
  purple: string;
  brown: string;
  gray: string;
}

// Theme context interface
interface ThemeContextType {
  colors: ColorPalette;
  getColor: (colorName: keyof ColorPalette) => string;
  getRandomColor: () => string;
  getAllColors: () => string[];
}

// Wedding Registry Theme Colors
const registryColors: ColorPalette = {
  coralPink: "#d58c86",
  forestGreen: "#2c3a2d",
  deepTeal: "#1a3030",
  burgundy: "#571612",
  wineRed: "#57142a",
  chocolate: "#2d0a00",
  purple: "#7f0de5",
  brown: "#5c190c",
  gray: "#848484",
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const getColor = (colorName: keyof ColorPalette): string => {
    return registryColors[colorName];
  };

  const getRandomColor = (): string => {
    const colorNames = Object.keys(registryColors) as (keyof ColorPalette)[];
    const randomIndex = Math.floor(Math.random() * colorNames.length);
    return registryColors[colorNames[randomIndex]];
  };

  const getAllColors = (): string[] => {
    return Object.values(registryColors);
  };

  const value: ThemeContextType = {
    colors: registryColors,
    getColor,
    getRandomColor,
    getAllColors,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
